import { ActionIcon, Badge, Button, Card, Grid, Group, NavLink, NumberInput, Select, Stack, Switch, Text, Textarea, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import type { FieldDef } from "../../components/DynamicFields";
import { FieldsEditor } from "../../components/FieldsEditor";
import { PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

interface Tpl { id: number; species_code: string; crop_group: string; name: string; version: number; updated_by?: string; stage_count?: number }
interface StageX { bbch_code: number; bbch_name?: string; mandatory: boolean; window_from_doy: number | null; window_to_doy: number | null; fields: FieldDef[]; instructions?: string | null }

const doyLabel = (d: number | null) => (d ? new Date(2025, 0, d).toLocaleDateString("sr-Latn-BA", { day: "numeric", month: "short" }) : "—");

export default function PhenoTemplatesPage() {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const edit = can("phenology:templates");
  const [sel, setSel] = useState<number | null>(null);
  const tpls = useQuery({ queryKey: ["pheno-tpls"], queryFn: () => http.get<Tpl[]>("/api/v1/phenology/templates") });
  const tpl = useQuery({ queryKey: ["pheno-tpl", sel], enabled: !!sel, queryFn: () => http.get<Tpl & { stages: StageX[] }>(`/api/v1/phenology/templates/${sel}`) });
  const bbch = useQuery({ queryKey: ["bbch", tpl.data?.crop_group], enabled: !!tpl.data, queryFn: () => http.get<{ code: number; name: string }[]>("/api/v1/phenology/bbch", { crop_group: tpl.data!.crop_group }) });
  const [stages, setStages] = useState<StageX[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (tpl.data) { setStages(tpl.data.stages); setDirty(false); } }, [tpl.data]);
  useEffect(() => { if (!sel && tpls.data?.length) setSel(tpls.data[0].id); }, [tpls.data, sel]);
  const save = useMutation({ mutationFn: () => http.put(`/api/v1/phenology/templates/${sel}/stages`, stages.map(({ bbch_name: _b, ...s }) => s)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pheno-tpl"] }); qc.invalidateQueries({ queryKey: ["pheno-tpls"] }); notifications.show({ color: "green", message: t("Nova verzija šablona sačuvana") }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  const set = (i: number, p: Partial<StageX>) => { setStages(stages.map((s, j) => (i === j ? { ...s, ...p } : s))); setDirty(true); };
  return (
    <AdminLayout>
      <>
        <PageHeader title="Fenološki šabloni" description="Obavezujuće BBCH faze i podaci koje proizvođač mora upisati po vrsti voća (Meier, 2018). Svaka izmjena povećava verziju šablona; proizvođač može dodavati sopstvena polja i faze, ali ne može ukloniti obavezne." />
        <Grid>
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder p={4}>
              {(tpls.data ?? []).map((x) => <NavLink key={x.id} label={x.name} description={`${x.crop_group} · v${x.version}`} active={sel === x.id} onClick={() => setSel(x.id)} />)}
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 9 }}>
            {tpl.data && (
              <Stack>
                <Group justify="space-between">
                  <div><Title order={4}>{tpl.data.name}</Title><Text size="xs" c="dimmed">{t("verzija")} {tpl.data.version} · {tpl.data.updated_by ?? t("sistem")}</Text></div>
                  {edit && <Group>
                    <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => { setStages([...stages, { bbch_code: 0, mandatory: false, window_from_doy: null, window_to_doy: null, fields: [{ key: "datum", label: "Datum", type: "date", required: true }] }]); setDirty(true); }}>{t("Faza")}</Button>
                    <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>{t("Sačuvaj novu verziju")}</Button></Group>}
                </Group>
                {stages.map((s, i) => (
                  <Card key={i} withBorder>
                    <Group justify="space-between" mb="xs" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        <Select w={300} searchable disabled={!edit} data={(bbch.data ?? []).map((b) => ({ value: String(b.code), label: `${b.code} — ${b.name}` }))}
                          value={String(s.bbch_code)} onChange={(v) => set(i, { bbch_code: Number(v) })} />
                        <Switch label={t("obavezna")} checked={s.mandatory} disabled={!edit} onChange={(e) => set(i, { mandatory: e.currentTarget.checked })} />
                      </Group>
                      <Group gap="xs" wrap="nowrap">
                        <NumberInput w={90} size="xs" label={t("od (dan)")} min={1} max={366} disabled={!edit} value={s.window_from_doy ?? ""} onChange={(v) => set(i, { window_from_doy: v === "" ? null : Number(v) })} />
                        <NumberInput w={90} size="xs" label={t("do (dan)")} min={1} max={366} disabled={!edit} value={s.window_to_doy ?? ""} onChange={(v) => set(i, { window_to_doy: v === "" ? null : Number(v) })} />
                        <Badge variant="light">{doyLabel(s.window_from_doy)} – {doyLabel(s.window_to_doy)}</Badge>
                        {edit && <ActionIcon color="red" variant="subtle" onClick={() => { setStages(stages.filter((_, j) => j !== i)); setDirty(true); }}><IconTrash size={14} /></ActionIcon>}
                      </Group>
                    </Group>
                    {edit ? <FieldsEditor fields={s.fields} onChange={(f) => set(i, { fields: f })} /> :
                      <Text size="sm">{s.fields.map((f) => `${f.label}${f.required ? "*" : ""}`).join(", ")}</Text>}
                    <Textarea mt="xs" size="xs" label={t("Uputstvo za unos")} disabled={!edit} autosize value={s.instructions ?? ""} onChange={(e) => set(i, { instructions: e.currentTarget.value })} />
                  </Card>
                ))}
              </Stack>
            )}
          </Grid.Col>
        </Grid>
      </>
    </AdminLayout>
  );
}
